/**
 * ModuleLoader - динамическая загрузка микрофронтендов
 *
 * Отвечает за:
 * 1. Динамический импорт entry.js модулей
 * 2. Загрузку CSS стилей модулей
 * 3. Монтирование модулей в DOM
 * 4. Управление жизненным циклом (mount/unmount)
 */

import type {
  ApiClient,
  EventBus,
  LoadedModule,
  ModuleExports,
  ModuleManifest,
  MountContext,
  OrgContext,
  ShellMode,
  UserContext,
} from './types'

/**
 * Состояние загрузки модуля
 */
export type ModuleLoadState = 'idle' | 'loading' | 'loaded' | 'mounted' | 'error'

/**
 * Информация о загруженном модуле
 */
export interface ModuleState {
  manifest: ModuleManifest
  loadState: ModuleLoadState
  exports?: ModuleExports
  container?: HTMLElement
  loadedAt?: string
  mountedAt?: string
  error?: Error
}

/**
 * Опции монтирования модуля
 */
export interface MountOptions {
  container: HTMLElement
  user?: UserContext
  org: OrgContext
  locale?: string
  theme?: 'light' | 'dark'
  mode?: ShellMode
  initialRoute?: string
}

/**
 * Класс для загрузки и монтирования микрофронтендов
 */
export class ModuleLoader {
  private modules = new Map<string, ModuleState>()
  private loadedStyles = new Set<string>()
  private bus: EventBus
  private apiClient: ApiClient

  constructor(bus: EventBus, apiClient: ApiClient) {
    this.bus = bus
    this.apiClient = apiClient
  }

  /**
   * Загрузить модуль (без монтирования)
   */
  async loadModule(manifest: ModuleManifest): Promise<ModuleExports> {
    const state = this.modules.get(manifest.id)

    // Уже загружен
    if (state?.loadState === 'loaded' || state?.loadState === 'mounted') {
      if (state.exports) {
        return state.exports
      }
    }

    // Уже загружается
    if (state?.loadState === 'loading') {
      // Ждём завершения загрузки
      return this.waitForLoad(manifest.id)
    }

    // Начинаем загрузку
    this.modules.set(manifest.id, {
      manifest,
      loadState: 'loading',
    })

    try {
      // Загружаем стили
      await this.loadStyles(manifest)

      // Динамический импорт entry.js
      const entryUrl = this.getEntryUrl(manifest)
      const moduleExports = (await import(/* @vite-ignore */ entryUrl)) as ModuleExports

      // Проверяем наличие обязательных экспортов
      if (typeof moduleExports.mountApp !== 'function') {
        throw new Error(`Module ${manifest.id} missing mountApp export`)
      }

      const loadedState: ModuleState = {
        manifest,
        loadState: 'loaded',
        exports: moduleExports,
        loadedAt: new Date().toISOString(),
      }

      this.modules.set(manifest.id, loadedState)

      // Эмитим событие о загрузке
      this.bus.emit('module:loaded', { moduleId: manifest.id })

      return moduleExports
    } catch (error) {
      const errorState: ModuleState = {
        manifest,
        loadState: 'error',
        error: error instanceof Error ? error : new Error(String(error)),
      }

      this.modules.set(manifest.id, errorState)

      // Эмитим событие об ошибке
      this.bus.emit('module:error', { moduleId: manifest.id, error })

      throw error
    }
  }

  /**
   * Монтировать модуль в DOM
   */
  async mountModule(manifest: ModuleManifest, options: MountOptions): Promise<void> {
    // Загружаем если ещё не загружен
    const exports = await this.loadModule(manifest)

    const state = this.modules.get(manifest.id)
    if (!state) {
      throw new Error(`Module ${manifest.id} state not found`)
    }

    // Формируем контекст монтирования
    const context: MountContext = {
      container: options.container,
      user: options.user,
      org: options.org,
      bus: this.bus,
      apiClient: this.apiClient,
      basePath: manifest.basePath,
      locale: options.locale ?? 'ru',
      theme: options.theme ?? 'dark',
      mode: options.mode ?? 'spa',
      initialRoute: options.initialRoute,
      getToken: () => this.getAuthToken(),
    }

    try {
      // Вызываем mountApp модуля
      await exports.mountApp(context)

      // Обновляем состояние
      this.modules.set(manifest.id, {
        ...state,
        loadState: 'mounted',
        container: options.container,
        mountedAt: new Date().toISOString(),
      })

      // Эмитим событие о монтировании
      this.bus.emit('module:mounted', { moduleId: manifest.id })
    } catch (error) {
      this.modules.set(manifest.id, {
        ...state,
        loadState: 'error',
        error: error instanceof Error ? error : new Error(String(error)),
      })

      this.bus.emit('module:error', { moduleId: manifest.id, error })

      throw error
    }
  }

  /**
   * Размонтировать модуль
   */
  unmountModule(moduleId: string): void {
    const state = this.modules.get(moduleId)
    if (!state) return

    if (state.loadState === 'mounted' && state.exports?.unmountAll) {
      try {
        state.exports.unmountAll()
      } catch (error) {
        console.warn(`[ModuleLoader] Failed to unmount ${moduleId}:`, error)
      }
    }

    // Очищаем контейнер
    if (state.container) {
      state.container.innerHTML = ''
    }

    // Обновляем состояние
    this.modules.set(moduleId, {
      ...state,
      loadState: 'loaded',
      container: undefined,
      mountedAt: undefined,
    })

    this.bus.emit('module:unmounted', { moduleId })
  }

  /**
   * Размонтировать все модули
   */
  unmountAll(): void {
    for (const [moduleId, state] of this.modules.entries()) {
      if (state.loadState === 'mounted') {
        this.unmountModule(moduleId)
      }
    }
  }

  /**
   * Получить состояние модуля
   */
  getModuleState(moduleId: string): ModuleState | undefined {
    return this.modules.get(moduleId)
  }

  /**
   * Проверить, загружен ли модуль
   */
  isLoaded(moduleId: string): boolean {
    const state = this.modules.get(moduleId)
    return state?.loadState === 'loaded' || state?.loadState === 'mounted'
  }

  /**
   * Проверить, смонтирован ли модуль
   */
  isMounted(moduleId: string): boolean {
    return this.modules.get(moduleId)?.loadState === 'mounted'
  }

  /**
   * Получить список загруженных модулей
   */
  getLoadedModules(): LoadedModule[] {
    const result: LoadedModule[] = []

    for (const state of this.modules.values()) {
      if ((state.loadState === 'loaded' || state.loadState === 'mounted') && state.exports) {
        result.push({
          manifest: state.manifest,
          version: state.manifest.version,
          exports: state.exports,
          loadedAt: state.loadedAt ?? new Date().toISOString(),
        })
      }
    }

    return result
  }

  /**
   * Preload модулей (загрузка без монтирования)
   */
  async preloadModules(manifests: ModuleManifest[]): Promise<void> {
    await Promise.all(
      manifests.map((manifest) =>
        this.loadModule(manifest).catch((error) => {
          console.warn(`[ModuleLoader] Failed to preload ${manifest.id}:`, error)
        })
      )
    )
  }

  // ============================================================================
  // Private helpers
  // ============================================================================

  /**
   * Получить URL entry.js с версией для cache busting
   */
  private getEntryUrl(manifest: ModuleManifest): string {
    const url = new URL(manifest.entryUrl, window.location.origin)
    url.searchParams.set('v', manifest.version)
    return url.toString()
  }

  /**
   * Загрузить CSS стили модуля
   */
  private async loadStyles(manifest: ModuleManifest): Promise<void> {
    const styleUrl = manifest.entryUrl.replace(/entry\.js$/, 'style.css')
    const styleId = `mf-style-${manifest.id}`

    // Уже загружены
    if (this.loadedStyles.has(manifest.id)) {
      return
    }

    // Проверяем, есть ли уже в DOM
    if (document.getElementById(styleId)) {
      this.loadedStyles.add(manifest.id)
      return
    }

    try {
      // Проверяем существование файла стилей
      const response = await fetch(styleUrl, { method: 'HEAD' })
      if (!response.ok) {
        // Стили не обязательны
        return
      }

      // Создаём link элемент
      const link = document.createElement('link')
      link.id = styleId
      link.rel = 'stylesheet'
      link.href = `${styleUrl}?v=${manifest.version}`

      // Ждём загрузки
      await new Promise<void>((resolve, reject) => {
        link.onload = () => resolve()
        link.onerror = () => reject(new Error(`Failed to load styles: ${styleUrl}`))
        document.head.appendChild(link)
      })

      this.loadedStyles.add(manifest.id)
    } catch {
      // Стили не критичны, продолжаем без них
      console.warn(`[ModuleLoader] Styles not found for ${manifest.id}`)
    }
  }

  /**
   * Ожидание завершения загрузки модуля
   */
  private waitForLoad(moduleId: string): Promise<ModuleExports> {
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        const state = this.modules.get(moduleId)

        if (state?.loadState === 'loaded' || state?.loadState === 'mounted') {
          clearInterval(checkInterval)
          if (state.exports) {
            resolve(state.exports)
          } else {
            reject(new Error(`Module ${moduleId} loaded but no exports`))
          }
        } else if (state?.loadState === 'error') {
          clearInterval(checkInterval)
          reject(state.error ?? new Error(`Module ${moduleId} failed to load`))
        }
      }, 50)

      // Таймаут 30 секунд
      setTimeout(() => {
        clearInterval(checkInterval)
        reject(new Error(`Module ${moduleId} load timeout`))
      }, 30000)
    })
  }

  /**
   * Получить токен авторизации
   * TODO: Интегрировать с auth store
   */
  private getAuthToken(): string | null {
    // Временная заглушка
    return localStorage.getItem('tlh:auth:token')
  }
}

// ============================================================================
// Factory function
// ============================================================================

/**
 * Создать экземпляр ModuleLoader
 */
export function createModuleLoader(bus: EventBus, apiClient: ApiClient): ModuleLoader {
  return new ModuleLoader(bus, apiClient)
}

export default ModuleLoader
